import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Building2, Loader2, Pencil, ShieldCheck, Trash2 } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/use-toast';
import { RoutesetterPicker } from '../../components/routesetters/RoutesetterPicker';
import { routeSettersService } from '../../services/route-setters';
import { useCurrentLocation } from '../hooks/useCurrentLocation';
import type { RouteSetter, RouteSetterRosterEntry } from '../../types/route-setter';

function initial(name: string): string {
  return name.trim().charAt(0).toUpperCase() || '?';
}

/**
 * Who this place credits with setting — the list the route setter picker opens
 * on before anybody types.
 *
 * Two kinds of row, and the page is mostly about telling them apart. Staff are
 * here because they hold the routesetter role, so there is nothing to remove:
 * the way off the list is to change the role. Everyone else was accredited
 * here explicitly — the freelancer, the visiting setter, the club member who
 * bolts the crag and has never opened the app — and those rows can be edited
 * and removed.
 *
 * Removing is deliberately weak. It says nothing about the work: the climbs
 * stay credited to whoever set them, and the setter still exists everywhere
 * else they are credited. All it changes is what this place is offered first.
 */
export default function RouteSettersPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { location, loading: locationLoading } = useCurrentLocation();
  const slug = location?.slug;

  const [roster, setRoster] = useState<RouteSetterRosterEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [renaming, setRenaming] = useState<RouteSetterRosterEntry | null>(null);
  const [newName, setNewName] = useState('');
  const [removing, setRemoving] = useState<RouteSetterRosterEntry | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(
    async (locationSlug: string) => {
      setLoading(true);
      try {
        setRoster(await routeSettersService.roster(locationSlug));
      } catch {
        toast({ title: t('gymAdmin.routeSetters.loadError'), variant: 'destructive' });
      } finally {
        setLoading(false);
      }
    },
    [t, toast]
  );

  useEffect(() => {
    if (slug) load(slug);
  }, [slug, load]);

  // The picker accredits whoever is chosen as a side effect of choosing them,
  // so this page only has to catch up afterwards.
  function handlePicked(next: RouteSetter[]) {
    if (next.length === 0 || !slug) return;
    load(slug);
  }

  async function confirmRename() {
    if (!renaming || !slug) return;
    setBusy(true);
    try {
      await routeSettersService.rename(slug, renaming.id, newName.trim());
      setRenaming(null);
      await load(slug);
      toast({ title: t('gymAdmin.routeSetters.renamed') });
    } catch (e) {
      toast({
        title: e instanceof Error ? e.message : t('gymAdmin.routeSetters.renameError'),
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
  }

  async function confirmRemove() {
    if (!removing || !slug) return;
    setBusy(true);
    try {
      await routeSettersService.remove(slug, removing.id);
      setRemoving(null);
      await load(slug);
      toast({ title: t('gymAdmin.routeSetters.removed') });
    } catch (e) {
      toast({
        title: e instanceof Error ? e.message : t('gymAdmin.routeSetters.removeError'),
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
  }

  if (locationLoading || (loading && roster.length === 0)) {
    return (
      <div className="space-y-3 p-4" data-testid="route-setters-loading">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  return (
    <div className="p-4 space-y-6 max-w-3xl" data-testid="route-setters-page">
      <div className="space-y-1">
        <h1 className="text-xl font-semibold">{t('gymAdmin.routeSetters.title')}</h1>
        <p className="text-sm text-muted-foreground">
          {t('gymAdmin.routeSetters.description')}
        </p>
      </div>

      <Card>
        <CardContent className="pt-4 space-y-2">
          <Label>{t('gymAdmin.routeSetters.addLabel')}</Label>
          <RoutesetterPicker
            mode="single"
            value={[]}
            onChange={handlePicked}
            locationId={slug ?? null}
            placeholder={t('gymAdmin.routeSetters.addPlaceholder')}
          />
        </CardContent>
      </Card>

      {roster.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('gymAdmin.routeSetters.empty')}</p>
      ) : (
        <ul className="border border-border rounded-md divide-y divide-border" data-testid="route-setters-list">
          {roster.map((setter) => (
            <li key={setter.id} className="flex items-center gap-3 px-3 py-2.5">
              <Avatar className="h-9 w-9 shrink-0">
                {setter.avatar_small_url && <AvatarImage src={setter.avatar_small_url} alt="" />}
                <AvatarFallback>{initial(setter.name)}</AvatarFallback>
              </Avatar>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium truncate">{setter.name}</span>
                  {setter.guest && (
                    <Badge variant="secondary" className="text-[10px]">
                      {t('gymAdmin.routeSetters.noAccount')}
                    </Badge>
                  )}
                  {setter.inherited ? (
                    <Badge variant="outline" className="text-[10px] gap-1">
                      <Building2 className="w-3 h-3" />
                      {t('gymAdmin.routeSetters.fromVenue')}
                    </Badge>
                  ) : (
                    !setter.accredited && (
                      <Badge variant="outline" className="text-[10px] gap-1">
                        <ShieldCheck className="w-3 h-3" />
                        {t('gymAdmin.routeSetters.fromStaff')}
                      </Badge>
                    )
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  {setter.username && <span className="mr-2">@{setter.username}</span>}
                  {t('gymAdmin.routeSetters.climbsCount', { count: setter.climbs_count })}
                </p>
              </div>

              {/* Staff rows carry no controls on purpose: their place on this
                  list is the role, and the role is edited on the staff page. */}
              {setter.accredited ? (
                <div className="flex items-center gap-1 shrink-0">
                  {setter.guest && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label={t('gymAdmin.routeSetters.rename', { name: setter.name })}
                      onClick={() => {
                        setRenaming(setter);
                        setNewName(setter.name);
                      }}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={t('gymAdmin.routeSetters.remove', { name: setter.name })}
                    onClick={() => setRemoving(setter)}
                  >
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
              ) : (
                <span className="text-xs text-muted-foreground shrink-0 max-w-40 text-right">
                  {t('gymAdmin.routeSetters.changeRoleInstead')}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}

      <Dialog open={!!renaming} onOpenChange={(open) => !open && setRenaming(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('gymAdmin.routeSetters.renameTitle')}</DialogTitle>
            {/* The setter is global, so the name is. Saying it here is cheaper
                than explaining it after somebody has renamed a crag's equipper
                from a gym. */}
            <DialogDescription>{t('gymAdmin.routeSetters.renameWarning')}</DialogDescription>
          </DialogHeader>
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            aria-label={t('gymAdmin.routeSetters.nameLabel')}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenaming(null)} disabled={busy}>
              {t('common.cancel')}
            </Button>
            <Button onClick={confirmRename} disabled={busy || !newName.trim()}>
              {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              {t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!removing} onOpenChange={(open) => !open && setRemoving(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t('gymAdmin.routeSetters.removeTitle', { name: removing?.name ?? '' })}
            </DialogTitle>
            <DialogDescription>
              {t('gymAdmin.routeSetters.removeWarning', { count: removing?.climbs_count ?? 0 })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRemoving(null)} disabled={busy}>
              {t('common.cancel')}
            </Button>
            <Button variant="destructive" onClick={confirmRemove} disabled={busy}>
              {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              {t('gymAdmin.routeSetters.removeConfirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
